import React, { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Modal } from './Modal';

/**
 * Confirmation destructive (passation design §5-§6) : réservée aux cas irréversibles.
 * 400 px, nomme ce qui va disparaître, une seule action pleine — rouge uniquement pour une
 * suppression définitive, le verbe sur le bouton (jamais « OK »).
 */
export interface ConfirmRequest {
    title: string;
    /** Ce qui disparaît, en clair : « 42 créneaux, 12 salariés et 2 renforts seront effacés ». */
    description: React.ReactNode;
    /** Le verbe : « Supprimer le planning », « Réinitialiser la semaine ». */
    confirmLabel: string;
    /** Rouge plein pour une suppression définitive ; noir pour une action lourde mais non destructive. */
    destructive?: boolean;
    /**
     * Case à cocher facultative, sous la description : l'interrupteur du moment (« envoyer le mail ? »),
     * demandé par Emeni le 19/09 pour pouvoir faire le geste sans prévenir personne. Le modèle est la
     * case « Envoyer la demande au prestataire » de la fenêtre « Booker les extras ».
     *
     * La réponse de confirmAction() reste un simple booléen — les 80 appels existants ne changent pas.
     * L'état de la case remonte par `onChange`, que l'appelant range dans une variable locale :
     *
     *   let notifier = defautDeLaFiche;
     *   const ok = await confirmAction({ …, checkbox: { label: '…', defaultChecked: notifier, onChange: (v) => { notifier = v; } } });
     *   if (!ok) return;
     */
    checkbox?: { label: string; sub?: string; defaultChecked: boolean; onChange: (v: boolean) => void };
    onConfirm: () => void | Promise<void>;
}

interface Props {
    request: ConfirmRequest | null;
    onClose: () => void;
}

export const ConfirmModal: React.FC<Props> = ({ request, onClose }) => {
    const [busy, setBusy] = useState(false);
    const [coche, setCoche] = useState(false);

    // Chaque nouvelle demande repart du défaut qu'elle porte — sans quoi la case garderait l'état
    // laissé par la confirmation précédente, et un mail partirait (ou ne partirait pas) par surprise.
    useEffect(() => {
        if (request?.checkbox) {
            setCoche(request.checkbox.defaultChecked);
            request.checkbox.onChange(request.checkbox.defaultChecked);
        }
    }, [request]);
    const run = async () => {
        if (!request) return;
        setBusy(true);
        try { await request.onConfirm(); } finally { setBusy(false); onClose(); }
    };
    const destructive = request?.destructive !== false;
    return (
        <Modal
            open={!!request}
            onClose={onClose}
            size="sm"
            title={request?.title}
            footer={
                <>
                    <button type="button" onClick={onClose} className="h-9 px-4 rounded-full border border-border bg-surface text-[13px] font-semibold text-content whitespace-nowrap shrink-0 hover:border-content">Annuler</button>
                    <button
                        type="button"
                        onClick={run}
                        disabled={busy}
                        data-autofocus
                        className={`h-9 px-4 rounded-full text-[13px] font-semibold whitespace-nowrap shrink-0 inline-flex items-center gap-1.5 hover:-translate-y-px transition-transform disabled:opacity-60 ${destructive ? 'bg-destructive text-white' : 'bg-content text-surface'}`}
                    >
                        {destructive && <Trash2 size={14} />} {busy ? 'En cours…' : request?.confirmLabel}
                    </button>
                </>
            }
        >
            <div className="text-[13px] text-content leading-relaxed">{request?.description}</div>
            {request?.checkbox && (
                <label className="mt-3 flex items-start gap-2 p-3 rounded-[10px] bg-surface-soft border border-border-soft cursor-pointer">
                    <input
                        type="checkbox"
                        checked={coche}
                        onChange={(e) => { setCoche(e.target.checked); request.checkbox?.onChange(e.target.checked); }}
                        className="accent-brand-600 w-4 h-4 mt-0.5 shrink-0"
                    />
                    <span className="min-w-0">
                        <span className="block text-[13px] font-semibold text-content">{request.checkbox.label}</span>
                        {request.checkbox.sub && <span className="block text-[12px] text-content-muted">{request.checkbox.sub}</span>}
                    </span>
                </label>
            )}
            {destructive && <p className="mt-3 text-[12px] text-content-muted">Cette action est définitive.</p>}
        </Modal>
    );
};
